import { searchTargetPath } from './search-navigation';

/**
 * What the ИКПУ / ЕНКТ code picker says when no code answers the query.
 *
 * Two different answers, because the two places mean different things
 * (owner, 2026-10-16):
 *
 * - **Onboarding and the saved-search sheet** — a saved search REQUIRES a code
 *   (or category / niche); `saved_searches` has no free-text column. A link
 *   out of there leaves the one task the screen exists for, and the person
 *   already believes they are searching every tender. So: a hint in their
 *   language — write one word for what you supply («мебель», «реклама»).
 * - **The catalogue's filter bar** — searching lot text IS a legitimate next
 *   step there, so it keeps a way out, worded as what it does («Такого кода
 *   нет — искать «q» по словам в лотах»), and it puts the words into the
 *   page's own search on the SAME page rather than navigating elsewhere.
 *
 * Fewer-than-three-live-codes gets nothing extra in either place: the API's
 * vocabulary arm already widens a thin list.
 */

/** The picker only searches from two characters; so does this. */
export const IKPU_FALLBACK_MIN_QUERY = 2;

/** True when the query is long enough to be searched and NO code came back. */
export function noIkpuCodeFound(query: string, hits: readonly unknown[]): boolean {
  return query.trim().length >= IKPU_FALLBACK_MIN_QUERY && hits.length === 0;
}

/** The subject axis the words REPLACE — category, code or niche. */
const SUBJECT_AXIS = ['category', 'ikpu', 'niche'] as const;

/**
 * The catalogue's params once the words go into its search: exactly what the
 * header search box submits ({@link searchTargetPath} — region / status / kind
 * / scope / budget / exclusions carried, `page` and sort dropped, `q` set),
 * minus the subject
 * axis — the words replace a code, they are not intersected with the old one.
 */
export function ikpuWordsSearchParams(
  query: string,
  current: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const carried = new URLSearchParams(current);
  for (const key of SUBJECT_AXIS) carried.delete(key);
  const path = searchTargetPath(query, carried);
  return new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?') + 1) : '');
}
